import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Badge,
  Box,
  Button,
  Flex,
  SimpleGrid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { brandPalette } from "../../../constants/brandPalette";
import CTable from "../../../components/Table/table";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import { adminSelectStyles } from "../../../components/Models/admin-modal-theme";
import { getHomeworkReportByChapterRequest } from "../../../modules/admin-modules/homework/Actions";
import { getAllChaptersRequest } from "../../../modules/admin-modules/chapters/Actions";
import { getAssistantsRequest } from "../../../modules/admin-modules/teachersAssistant/Actions";
import Select from "react-select";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { RiFileExcel2Line } from "react-icons/ri";

function sanitizeExcelSheetName(name) {
  let s = String(name);
  for (const c of ["\\", "/", "*", "?", ":", "[", "]"]) {
    s = s.split(c).join(" ");
  }
  s = s.trim().replace(/\s+/g, " ");
  return (s || "Sheet").slice(0, 31);
}

const selectBase = adminSelectStyles({ menuZIndex: 1600 });

const selectStyles = {
  ...selectBase,
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
  control: (styles, state) => ({
    ...selectBase.control(styles, state),
    borderRadius: "14px",
    minHeight: "44px",
  }),
};

const cardSx = {
  border: "1px solid",
  borderColor: "var(--border-subtle)",
  bg: "var(--surface-raised)",
  borderRadius: "18px",
  boxShadow: "0 18px 55px rgba(0,0,0,0.12)",
  color: "var(--text-primary)",
};

function StatusBadge({ isPositive, label }) {
  return (
    <Badge
      px={2}
      py={0.5}
      borderRadius="md"
      fontWeight="700"
      fontSize="xs"
      bg={isPositive ? "rgba(0, 170, 141, 0.12)" : "rgba(155, 44, 44, 0.12)"}
      color={isPositive ? "#00AA8D" : "#9B2C2C"}
    >
      {label}
    </Badge>
  );
}

const HomeworkReportByChapter = () => {
  const dispatch = useDispatch();
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const setPage = () => {};

  const homeworkReportData = useSelector(
    (state) => state.homework.homeworkReportByChapter
  );
  const isLoading = useSelector((state) => state.homework.isLoading);
  const chapters = useSelector((state) => state.chapters.chapters);
  const assistants = useSelector((state) => state.teacherassistants.assistants);

  useEffect(() => {
    dispatch(getAllChaptersRequest());
    dispatch(getAssistantsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (selectedChapter) {
      dispatch(getHomeworkReportByChapterRequest(selectedChapter.value));
    }
  }, [selectedChapter, dispatch]);

  const chapterOptions = useMemo(
    () =>
      chapters?.map((chapter) => ({
        value: chapter.id,
        label: chapter.name,
      })) ?? [],
    [chapters]
  );

  const homeworkNames = useMemo(() => {
    if (!homeworkReportData?.data || typeof homeworkReportData.data !== "object") {
      return [];
    }
    return Object.keys(homeworkReportData.data);
  }, [homeworkReportData?.data]);

  const assistantStats = useMemo(() => {
    if (!homeworkReportData?.data) return { stats: {}, totalChecked: 0 };

    const stats = {};
    let totalChecked = 0;

    try {
      Object.values(homeworkReportData.data).forEach((homeworkRecords) => {
        if (Array.isArray(homeworkRecords)) {
          homeworkRecords.forEach((record) => {
            if (record.assistant) {
              const assistantName = record.assistant.name || "Unknown";
              stats[assistantName] = (stats[assistantName] || 0) + 1;
              totalChecked++;
            }
          });
        }
      });
    } catch (error) {
      console.error("Error processing assistant stats:", error);
    }

    return { stats, totalChecked };
  }, [homeworkReportData?.data]);

  const getCurrentTabData = () => {
    if (!homeworkReportData?.data || homeworkNames.length === 0) return [];

    const currentHomeworkName = homeworkNames[activeTab];
    const homeworkRecords = homeworkReportData.data[currentHomeworkName] || [];

    try {
      return homeworkRecords.map((record, index) => ({
        ...record,
        index: `#${index + 1}`,
        student_name: record.student?.name || "N/A",
        student_username: record.student?.user_name || "N/A",
        evaluation: record.evaluation || "No evaluation",
        assistant_comment: record.assistant_comment || "No comment",
        assistant_name: record.assistant?.name || "No assistant",
        status_badge: (
          <StatusBadge
            isPositive={record.status === 1}
            label={record.status === 1 ? "Submitted" : "Not submitted"}
          />
        ),
        solved_badge: (
          <StatusBadge
            isPositive={record.is_solved === 1}
            label={record.is_solved === 1 ? "Solved" : "Not solved"}
          />
        ),
      }));
    } catch (error) {
      console.error("Error processing current tab data:", error);
      return [];
    }
  };

  const currentTabData = getCurrentTabData();

  useEffect(() => {
    if (homeworkNames.length > 0 && activeTab >= homeworkNames.length) {
      setActiveTab(0);
    }
  }, [homeworkNames.length, activeTab]);

  const columns = [
    { Header: "ID", accessor: "index" },
    { Header: "Student", accessor: "student_name" },
    { Header: "Username", accessor: "student_username" },
    { Header: "Evaluation", accessor: "evaluation" },
    { Header: "Assistant comment", accessor: "assistant_comment" },
    { Header: "Assistant", accessor: "assistant_name" },
    { Header: "Status", accessor: "status_badge" },
    { Header: "Solved", accessor: "solved_badge" },
  ];

  const exportToExcel = async () => {
    if (!homeworkReportData?.data) return;

    const workbook = new ExcelJS.Workbook();

    Object.entries(homeworkReportData.data).forEach(([homeworkName, records]) => {
      if (!Array.isArray(records) || records.length === 0) return;

      const sheet = workbook.addWorksheet(sanitizeExcelSheetName(homeworkName));

      sheet.columns = [
        { header: "Student Name", key: "studentName", width: 25 },
        { header: "Student Username", key: "studentUsername", width: 15 },
        { header: "Evaluation", key: "evaluation", width: 50 },
        { header: "Assistant Comment", key: "assistantComment", width: 30 },
        { header: "Assistant Name", key: "assistantName", width: 25 },
      ];

      records.forEach((record) => {
        sheet.addRow({
          studentName: record.student?.name || "N/A",
          studentUsername: record.student?.user_name || "N/A",
          evaluation: record.evaluation || "No evaluation",
          assistantComment: record.assistant_comment || "No comment",
          assistantName: record.assistant?.name || "No assistant",
        });
      });

      const sheetAssistantStats = {};
      records.forEach((record) => {
        if (record.assistant?.name) {
          const name = record.assistant.name;
          sheetAssistantStats[name] = (sheetAssistantStats[name] || 0) + 1;
        }
      });

      if (Object.keys(sheetAssistantStats).length > 0) {
        sheet.addRow({});
        sheet.addRow({ studentName: "Assistant statistics", studentUsername: "" });
        Object.entries(sheetAssistantStats).forEach(([name, count]) => {
          sheet.addRow({ studentName: name, studentUsername: count });
        });
      }
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const fileName = `homework-report-${
      selectedChapter?.label || "chapter"
    }-${new Date().toISOString().split("T")[0]}.xlsx`;
    saveAs(blob, fileName);
  };

  const assistantOverview = assistants?.data ?? [];

  return (
    <Layout>
      <Box
        minH="400px"
        maxW="1440px"
        mx="auto"
        w="100%"
        px={{ base: 4, sm: 5, md: 8, lg: 10 }}
        pt={{ base: 6, md: 8 }}
        pb={{ base: 10, md: 12 }}
        dir="ltr"
      >
        <Flex
          w="100%"
          mb={8}
          justifyContent="space-between"
          alignItems={{ base: "stretch", md: "flex-start" }}
          gap={4}
          flexWrap="wrap"
        >
          <Box flex="1" minW="0">
            <Text
              fontSize={{ base: "28px", md: "40px" }}
              fontWeight="800"
              color={brandPalette.primary}
              fontFamily="'Amiri', 'Tajawal', sans-serif"
              lineHeight="1.1"
              sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
            >
              Homework Comprehensive Report
            </Text>
            <Text mt={2} fontSize="sm" fontWeight="600" color="var(--text-muted)" maxW="2xl">
              Review homework submissions by chapter, assistant feedback, and grading status across
              all assignments in the selected lesson.
            </Text>
          </Box>
          <Flex
            align="flex-start"
            gap={3}
            flexWrap="wrap"
            justifyContent={{ base: "flex-start", md: "flex-end" }}
          >
            <Box minW={{ base: "100%", md: "280px" }} maxW={{ base: "100%", md: "360px" }}>
              <Select
                options={chapterOptions}
                isMulti={false}
                onChange={setSelectedChapter}
                styles={selectStyles}
                placeholder="Select chapter"
                value={selectedChapter}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
                isClearable
              />
            </Box>
            {homeworkNames.length > 0 ? (
              <Button
                onClick={exportToExcel}
                leftIcon={<RiFileExcel2Line />}
                bg={brandPalette.primary}
                color={brandPalette.cream}
                fontWeight="800"
                borderRadius="full"
                px={6}
                _hover={{ bg: brandPalette.accentDark }}
              >
                Export to Excel
              </Button>
            ) : null}
            <LogoutMenu />
          </Flex>
        </Flex>

        {assistantOverview.length > 0 ? (
          <Box {...cardSx} p={{ base: 4, md: 6 }} mb={6}>
            <Text fontSize="md" fontWeight="800" color="var(--text-primary)" mb={4}>
              Assistant overview
            </Text>
            <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={4}>
              {assistantOverview.map((assistant) => (
                <Box
                  key={assistant?.id}
                  p={4}
                  bg="var(--surface-muted)"
                  borderWidth="1px"
                  borderColor="var(--border-subtle)"
                  borderRadius="lg"
                >
                  <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" textTransform="uppercase" mb={1}>
                    Assistant
                  </Text>
                  <Text fontSize="sm" fontWeight="700" color="var(--text-primary)" noOfLines={2} mb={2}>
                    {assistant?.name}
                  </Text>
                  <Text fontSize="2xl" fontWeight="800" color={brandPalette.primary} lineHeight="1">
                    {assistant?.homework_count || 0}
                  </Text>
                  <Text fontSize="xs" color="var(--text-muted)" mt={1}>
                    Homeworks checked
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        ) : null}

        {Object.keys(assistantStats.stats).length > 0 ? (
          <Box {...cardSx} p={{ base: 4, md: 6 }} mb={6}>
            <Flex align="baseline" justify="space-between" gap={3} mb={4} flexWrap="wrap">
              <Text fontSize="md" fontWeight="800" color="var(--text-primary)">
                Chapter assistant activity
              </Text>
              <Badge
                bg="rgba(91, 13, 22, 0.1)"
                color={brandPalette.primary}
                px={3}
                py={1}
                borderRadius="full"
                fontWeight="800"
              >
                {assistantStats.totalChecked} reviewed
              </Badge>
            </Flex>
            <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={4}>
              {Object.entries(assistantStats.stats).map(([assistantName, count]) => (
                <Box
                  key={assistantName}
                  p={4}
                  bg="var(--surface-muted)"
                  borderWidth="1px"
                  borderColor="var(--border-subtle)"
                  borderRadius="lg"
                >
                  <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" textTransform="uppercase" mb={1}>
                    Assistant
                  </Text>
                  <Text fontSize="sm" fontWeight="700" color="var(--text-primary)" noOfLines={2} mb={2}>
                    {assistantName}
                  </Text>
                  <Text fontSize="2xl" fontWeight="800" color={brandPalette.primary} lineHeight="1">
                    {count}
                  </Text>
                  <Text fontSize="xs" color="var(--text-muted)" mt={1}>
                    In this chapter
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        ) : null}

        {homeworkNames.length > 0 ? (
          <Box {...cardSx} p={{ base: 4, md: 6 }}>
            <Tabs
              index={activeTab}
              onChange={setActiveTab}
              variant="unstyled"
              isLazy
            >
              <TabList flexWrap="wrap" gap={2} mb={6}>
                {homeworkNames.map((homeworkName, index) => (
                  <Tab
                    key={homeworkName}
                    px={4}
                    py={2}
                    borderRadius="full"
                    fontWeight="700"
                    fontSize="sm"
                    borderWidth="1px"
                    borderColor="var(--border-subtle)"
                    bg="var(--surface-muted)"
                    color="var(--text-muted)"
                    _selected={{
                      bg: brandPalette.primary,
                      color: brandPalette.cream,
                      borderColor: brandPalette.primary,
                    }}
                    _hover={{
                      borderColor: brandPalette.primary,
                      color: "var(--text-primary)",
                    }}
                  >
                    {homeworkName}
                  </Tab>
                ))}
              </TabList>
              <TabPanels>
                {homeworkNames.map((homeworkName) => (
                  <TabPanel key={homeworkName} px={0}>
                    <CTable
                      selectedData={() => {}}
                      Columns={columns}
                      Data={getCurrentTabData()}
                      Actions
                      currentpage={pageNumber}
                      setPage={setPage}
                      setPageNumber={setPageNumber}
                      totalPage={Math.ceil(currentTabData.length / 10) || 1}
                      isLoading={isLoading}
                      wrapperProps={{
                        borderWidth: "1px",
                        borderColor: "var(--border-subtle)",
                        borderRadius: "12px",
                        overflow: "hidden",
                      }}
                    />
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          </Box>
        ) : null}

        {(!homeworkReportData?.data ||
          (homeworkReportData?.data &&
            Object.keys(homeworkReportData.data).length === 0)) &&
        !isLoading ? (
          <Box {...cardSx} p={10} textAlign="center">
            <Text fontSize="md" fontWeight="700" color="var(--text-muted)">
              {selectedChapter
                ? "No homework data found for the selected chapter."
                : "Select a chapter to load the homework report."}
            </Text>
          </Box>
        ) : null}

        {isLoading ? (
          <Box {...cardSx} p={10} textAlign="center">
            <Text fontSize="md" fontWeight="700" color="var(--text-primary)">
              Loading report…
            </Text>
          </Box>
        ) : null}
      </Box>
    </Layout>
  );
};

export default HomeworkReportByChapter;
